// src/context/BrandingContext.jsx
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
} from "react";
import { gql, useQuery } from "@apollo/client";
import { getSubdomain } from "../util/subdomain";

const ORG_PORTAL_BRANDING = gql`
    query OrgPortalBranding {
        orgPortalBranding {
            organization {
                id
                name
                logoUrl
                slug
                activeSubscription {
                    plan {
                        id
                        name
                        features {
                            key
                        }
                    }
                }
            }
            primaryColor
            secondaryColor
            accentColor
            backgroundColor
            surfaceColor
            borderColor
            textOnBackground
            textOnSurface
            textOnPrimary
            textOnSecondary
            logoUrl
        }
    }
`;

/**
 * Base fallback theme (kBola default)
 */
const baseTheme = {
    colors: {
        // Shell / layout
        background: "#14151a",
        backgroundAlt: "#0b0d15",
        surface: "#1f1f2e",
        surfaceAlt: "#1f1f2e",
        border: "#1f2937",
        borderSubtle: "rgba(255,255,255,0.08)",

        // Brand accents
        primary: "#00adff",
        primarySoft: "rgba(8, 160, 250, 0.15)",
        accent: "#FF5F1F",
        secondary: "#006CA8",

        // Text
        textOnBackground: "#ffffff",
        textOnSurface: "#ffffff",
        textOnPrimary: "#14151a",
        textOnSecondary: "#ffffff",
        textMuted: "#9B9B9B",
        angeonBlue: "#00adff",
        angeonBlueDark: "#006CA8",
        angeonBlueSoft: "rgba(8, 160, 250, 0.15)",
        canary: "#00adff",
        woodsmoke: "#14151a",
        kBolaBlue: "#14151a",
        shark: "#1f1f2e",
        sulu: "#00adff",

        manatee: "#9ca3af",
        white: "#ffffff",
        lightBg: "#ffffff",
        lightBgAlt: "#E2E8F0",
        lightSurface: "#EEF3F8",
        lightMuted: "#4B5563",
        lightBorder: "#D1DCE8",
        overlayModal: "rgba(0,0,0,0.6)",
        // You still have direct hex access if needed:
        pablo: "#787369",
        battleshipGray: "#8f9f74",
        fuscousGray: "#524d47",
        chaletGreen: "#57663c",
        leather: "#9e715a",
        midGray: "#585a64",
        kelp: "#414e30",

        // Status colors – keep static for now (can be themed later if needed)
        lose: "#a50c0c",
        win: "#309834",
        warning: "#FFC107",
        danger: "#FF3333",
        success: "#4CAF50",
        lightSuccess: "#eefbee",
        info: "#17a2b8",
        link: "#8f8ff4",
    },
    logo: {
        main: "/dist/img/angeonx-sports.png",
        short: "/dist/img/angeonx-sports.png",
    },
};

/**
 * Build effective theme from branding + baseTheme
 */
function buildOrgTheme(branding) {
    if (!branding) {
        return { ...baseTheme };
    }

    const {
        primaryColor,
        secondaryColor,
        accentColor,
        backgroundColor,
        surfaceColor,
        borderColor,
        textOnBackground,
        textOnSurface,
        textOnPrimary,
        textOnSecondary,
        logoUrl,
        organization,
    } = branding;

    const colors = {
        ...baseTheme.colors,
        ...(backgroundColor && { background: backgroundColor }),
        ...(surfaceColor && { surface: surfaceColor }),
        ...(borderColor && { border: borderColor }),

        ...(primaryColor && { primary: primaryColor }),
        ...(secondaryColor && { secondary: secondaryColor }),
        ...(accentColor && { accent: accentColor }),

        ...(textOnBackground && { textOnBackground }),
        ...(textOnSurface && { textOnSurface }),
        ...(textOnPrimary && { textOnPrimary }),
        ...(textOnSecondary && { textOnSecondary }),
    };

    const logo = {
        // ...baseTheme.logo,
        // Branding-specific logo
        ...(logoUrl && { main: logoUrl, short: logoUrl }),
        // Prefer organization logo for main if present
        ...(organization?.logoUrl && { main: organization.logoUrl }),
    };

    return { colors, logo };
}

/**
 * Helper: set favicon globally
 */
function setFavicon(href) {
    if (typeof document === "undefined" || !href) return;

    let link =
        document.querySelector("link[rel='icon']") ||
        document.createElement("link");

    link.rel = "icon";
    link.href = href;
    document.head.appendChild(link);
}

function hexToRgbComponents(hex) {
    if (!hex || typeof hex !== "string") return null;

    const normalized = hex.trim().replace("#", "");
    const isShortHex = normalized.length === 3;
    const isLongHex = normalized.length === 6;

    if (!isShortHex && !isLongHex) return null;

    const fullHex = isShortHex
        ? normalized
            .split("")
            .map((char) => `${char}${char}`)
            .join("")
        : normalized;

    const value = parseInt(fullHex, 16);
    if (Number.isNaN(value)) return null;

    return [
        (value >> 16) & 255,
        (value >> 8) & 255,
        value & 255,
    ].join(",");
}

/**
 * Push theme colors into CSS variables for GlobalStyles
 */
function ThemeCssVariables({ theme }) {
    useEffect(() => {
        if (typeof document === "undefined") return;

        const root = document.documentElement;
        const { colors } = theme || {};
        if (!colors) return;

        // Core layout tokens
        if (colors.background)
            root.style.setProperty("--kbs-background", colors.background);
        if (colors.backgroundAlt)
            root.style.setProperty("--kbs-background-alt", colors.backgroundAlt);
        if (colors.surface)
            root.style.setProperty("--kbs-surface", colors.surface);
        if (colors.border)
            root.style.setProperty("--kbs-border", colors.border);

        // Brand colors
        if (colors.primary) {
            root.style.setProperty("--kbs-primary", colors.primary);
            root.style.setProperty(
                "--kbs-primary-rgb",
                hexToRgbComponents(colors.primary) || "0,173,255"
            );
        }
        if (colors.secondary) {
            root.style.setProperty("--kbs-secondary", colors.secondary);
            root.style.setProperty(
                "--kbs-secondary-rgb",
                hexToRgbComponents(colors.secondary) || "0,108,168"
            );
        }
        if (colors.accent)
            root.style.setProperty("--kbs-accent", colors.accent);

        // Text tokens
        if (colors.textOnBackground)
            root.style.setProperty(
                "--kbs-text-on-background",
                colors.textOnBackground
            );
        if (colors.textOnSurface)
            root.style.setProperty(
                "--kbs-text-on-surface",
                colors.textOnSurface
            );
        if (colors.textOnPrimary) {
            root.style.setProperty(
                "--kbs-text-on-primary",
                colors.textOnPrimary
            );
            root.style.setProperty(
                "--kbs-text-on-primary-rgb",
                hexToRgbComponents(colors.textOnPrimary) || "20,21,26"
            );
        }
        if (colors.textOnSecondary)
            root.style.setProperty(
                "--kbs-text-on-secondary",
                colors.textOnSecondary
            );
        if (colors.textMuted)
            root.style.setProperty("--kbs-text-muted", colors.textMuted);
    }, [theme]);

    return null;
}

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
    const hostname =
        typeof window !== "undefined" ? window.location.hostname : "";
    const search =
        typeof window !== "undefined" ? window.location.search : "";
    const subdomain = getSubdomain(hostname, search);

    const initialIsOrgPortal = !!subdomain;

    const { data, loading, error } = useQuery(ORG_PORTAL_BRANDING, {
        skip: !initialIsOrgPortal,
    });

    const branding = data?.orgPortalBranding || null;
    const organization = branding?.organization || null;
    const theme = useMemo(() => {
        const built = buildOrgTheme(branding);
        return built || baseTheme;
    }, [branding]);

    const value = useMemo(
        () => ({
            branding,
            theme,
            organization,
            isOrgPortal: initialIsOrgPortal && !!branding,
            // ready = we've decided if it's org portal or not
            ready: !initialIsOrgPortal || (!loading && !error),
            loading,
            error,
        }),
        [branding, theme, organization, initialIsOrgPortal, loading, error]
    );

    /**
     * Redirect invalid org subdomain back to base domain
     */
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!initialIsOrgPortal) return;
        if (loading) return;

        const invalidOrg = !!error || !branding;
        if (!invalidOrg) return;

        const host = window.location.hostname;
        const parts = host.split(".");
        const rootDomain =
            parts.length >= 2 ? parts.slice(-2).join(".") : host;
        const fallbackUrl = rootDomain === "angeonx.com"
            ? "https://sports.angeonx.com"
            : `https://${rootDomain}`;

        window.location.replace(fallbackUrl);
    }, [initialIsOrgPortal, loading, error, branding]);

    /**
     * 🔹 Global org-level head: title + favicon
     * Runs whenever `branding` changes.
     */
    useEffect(() => {
        if (typeof document === "undefined") return;

        const defaultTitle = "AngeonX Sports – Sports Management Platform";
        const defaultFavicon = "/favicon.png";

        // No org (root domain) → default head
        if (!branding) {
            document.title = defaultTitle;
            setFavicon(defaultFavicon);
            return;
        }

        const orgName = branding.organization?.name || "Organization";

        // Base org-level title; pages can still override/append if needed
        document.title = `${orgName} – Powered by AngeonX Sports`;

        const iconHref =
            branding.logoUrl ||
            branding.organization?.logoUrl ||
            defaultFavicon;

        setFavicon(iconHref);
    }, [branding]);

    return (
        <BrandingContext.Provider value={value}>
            {/* CSS variables for GlobalStyles */}
            <ThemeCssVariables theme={theme} />
            {children}
        </BrandingContext.Provider>
    );
}

export function useBranding() {
    const ctx = useContext(BrandingContext);
    if (!ctx) {
        throw new Error("useBranding must be used within BrandingProvider");
    }
    return ctx;
}
